import {
  Button,
  Modal,
  MultiSelect,
  PasswordInput,
  Stack,
  Table,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { PhoneInput } from "../../components/PhoneInput";
import { CitySelect } from "../../components/CitySelect";
import { CITY_SELECT_COPY } from "../../lib/cities";
import { SegmentOverflow } from "../../components/SegmentOverflow";
import { DataTableShell, PageHeader, SortableTh, StatusBadge, useTableSort, useVisibleWindow } from "../../components/ui";
import { companyApi, consultancyApi } from "../../lib/api";
import { ApiError } from "../../lib/apiClient";
import { NAV_COPY, NAV_ICONS } from "../../lib/navCopy";
import { SEGMENT_SELECT_COPY } from "../../lib/segments";
import { toPhoneForApi, validateIndianPhoneFieldOptional } from "../../lib/phone";
import { useSegmentCatalog } from "../../lib/useSegmentCatalog";

export function ConsultancyRecruitersPage() {
  const queryClient = useQueryClient();
  const [inviteOpen, setInviteOpen] = useState(false);
  const { flatOptions: segmentOptions } = useSegmentCatalog();

  const companyQuery = useQuery({ queryKey: ["my-company"], queryFn: companyApi.myCompany });
  const isAdmin = companyQuery.data?.company_role === "company_admin";

  const recruitersQuery = useQuery({
    queryKey: ["consultancy-recruiters"],
    queryFn: consultancyApi.listRecruiters,
  });

  const form = useForm({
    initialValues: {
      full_name: "",
      email: "",
      password: "",
      phone: "",
      city: "",
      segments: [] as string[],
    },
    validate: {
      full_name: (v) => (v.trim().length >= 2 ? null : "Required"),
      email: (v) => (/^\S+@\S+\.\S+$/.test(v) ? null : "Valid email required"),
      password: (v) => (v.length >= 8 ? null : "Min 8 characters"),
      phone: validateIndianPhoneFieldOptional,
      city: (v) => (v.trim() ? null : CITY_SELECT_COPY.requiredError),
      segments: (v) => (v.length > 0 ? null : SEGMENT_SELECT_COPY.requiredError),
    },
  });

  const inviteMutation = useMutation({
    mutationFn: (values: typeof form.values) =>
      consultancyApi.inviteRecruiter({
        full_name: values.full_name,
        email: values.email,
        password: values.password,
        phone: values.phone ? toPhoneForApi(values.phone) : undefined,
        city: values.city || undefined,
        segments: values.segments,
      }),
    onSuccess: () => {
      notifications.show({ color: "green", title: "Invited", message: "Recruiter added to your consultancy." });
      queryClient.invalidateQueries({ queryKey: ["consultancy-recruiters"] });
      form.reset();
      setInviteOpen(false);
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        title: "Failed",
        message: err instanceof ApiError ? err.message : "Something went wrong",
      });
    },
  });

  const recruiters = recruitersQuery.data ?? [];
  const { sorted, sortKey, direction, toggle } = useTableSort(recruiters, {
    name: (r) => r.user.full_name,
    email: (r) => r.user.email,
    kyc: (r) => r.kyc_status,
  });
  const recruiterWindow = useVisibleWindow(sorted);

  return (
    <Stack gap="lg">
      <PageHeader
        icon={NAV_ICONS.recruiters}
        title={NAV_COPY.consultancy.recruiters}
        subtitle="People who submit candidates under your consultancy rate card."
        actions={
          isAdmin ? (
            <Button color="violet" onClick={() => setInviteOpen(true)}>
              Invite recruiter
            </Button>
          ) : undefined
        }
      />

      <DataTableShell
        loading={recruitersQuery.isLoading}
        error={
          recruitersQuery.isError
            ? recruitersQuery.error instanceof Error
              ? recruitersQuery.error.message
              : undefined
            : undefined
        }
        onRetry={() => void recruitersQuery.refetch()}
        hasMore={recruiterWindow.hasMore}
        loadingMore={recruiterWindow.loadingMore}
        onLoadMore={recruiterWindow.loadMore}
        isEmpty={recruiters.length === 0}
        empty={{
          icon: NAV_ICONS.recruiters,
          title: "No recruiters yet",
          description: isAdmin ? "Invite recruiters so they can work marketplace jobs for you." : undefined,
        }}
      >
        <Table verticalSpacing="md" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <SortableTh columnKey="name" sortKey={sortKey} direction={direction} onSort={toggle}>
                Name
              </SortableTh>
              <SortableTh columnKey="email" sortKey={sortKey} direction={direction} onSort={toggle}>
                Email
              </SortableTh>
              <Table.Th>Segments</Table.Th>
              <SortableTh columnKey="kyc" sortKey={sortKey} direction={direction} onSort={toggle}>
                KYC
              </SortableTh>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {recruiterWindow.items.map((row) => (
              <Table.Tr key={row.id}>
                <Table.Td fw="var(--axio-font-weight-medium)">{row.user.full_name}</Table.Td>
                <Table.Td>{row.user.email}</Table.Td>
                <Table.Td>
                  <SegmentOverflow segments={row.segments ?? []} />
                </Table.Td>
                <Table.Td>
                  <StatusBadge status={row.kyc_status} />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </DataTableShell>

      <Modal opened={inviteOpen} onClose={() => setInviteOpen(false)} title="Invite recruiter" centered>
        <form onSubmit={form.onSubmit((values) => inviteMutation.mutate(values))}>
          <Stack>
            <TextInput label="Full name" required {...form.getInputProps("full_name")} />
            <TextInput label="Email" required {...form.getInputProps("email")} />
            <PhoneInput
              label="Phone (WhatsApp)"
              value={form.values.phone}
              onChange={(value) => form.setFieldValue("phone", value)}
              error={form.errors.phone}
            />
            <CitySelect
              label={CITY_SELECT_COPY.label}
              withAsterisk
              clearable={false}
              value={form.values.city}
              onChange={(city) => form.setFieldValue("city", city)}
              error={form.errors.city}
            />
            <PasswordInput label="Password" required {...form.getInputProps("password")} />
            <MultiSelect
              label="Segments"
              withAsterisk
              data={segmentOptions}
              searchable
              {...form.getInputProps("segments")}
            />
            <Button type="submit" color="violet" loading={inviteMutation.isPending}>
              Invite
            </Button>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}
